<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="840"
  >
    <v-card class="onboarding-wizard">
      <v-card-text class="pa-8">
        <v-progress-circular
          v-if="isLoading"
          indeterminate
          color="primary"
          class="d-block mx-auto my-8"
        />

        <template v-else>
          <!-- Header Section -->
          <div class="d-flex flex-column align-center mb-8">
            <v-img
              :src="brand.logo"
              width="180"
              class="mb-6"
            />
            <h2 class="text-h5 font-weight-bold mb-2">
              {{ stepTitle }}
            </h2>
            <p class="text-body-1 text-medium-emphasis text-center">
              {{ stepSubtitle }}
            </p>
          </div>

          <!-- Progress Indicator -->
          <div 
            v-if="activeSteps.length > 1"
            class="d-flex justify-center mb-8"
          >
            <div class="stepper-container">
              <div class="d-flex align-center">
                <template
                  v-for="(stepName, index) in activeSteps"
                  :key="stepName"
                >
                  <!-- Step -->
                  <div 
                    class="stepper-step d-flex flex-column align-center"
                    :class="{ 
                      'completed': stepNumbers[currentStepName] > stepNumbers[stepName], 
                      'active': currentStepName === stepName
                    }"
                  >
                    <div class="step-circle d-flex align-center justify-center">
                      <v-icon
                        v-if="stepNumbers[currentStepName] > stepNumbers[stepName]"
                        size="16"
                        color="white"
                      >
                        mdi-check
                      </v-icon>
                      <v-icon
                        v-else-if="currentStepName === stepName"
                        size="16"
                        :color="'primary'"
                      >
                        mdi-pencil
                      </v-icon>
                      <span v-else>{{ stepNumbers[stepName] }}</span>
                    </div>
                    <div class="step-content mt-2 text-center">
                      <span class="step-label text-body-2 font-weight-medium d-block">
                        {{ stepName }}
                      </span>
                      <span 
                        v-if="stepName === 'Profile' && organization.name && stepNumbers[currentStepName] > stepNumbers[stepName]" 
                        class="step-detail text-caption text-medium-emphasis"
                      >
                        {{ organization.name }}
                      </span>
                    </div>
                  </div>

                  <!-- Connector (if not last step) -->
                  <div 
                    v-if="index < activeSteps.length - 1"
                    class="step-connector"
                    :class="{ 'completed': stepNumbers[currentStepName] > stepNumbers[stepName] }"
                  />
                </template>
              </div>
            </div>
          </div>

          <v-window
            v-model="step"
            class="onboarding-window"
          >
            <!-- Click-through Agreement (SD-19-05 hard gate) -->
            <v-window-item
              v-if="stepNumbers['Agreement']"
              :value="stepNumbers['Agreement']"
            >
              <AgreementStep
                ref="agreementStep"
                v-model="agreementConsent"
                :required="needsAgreementAcceptance"
              />
            </v-window-item>

            <!-- Profile -->
            <v-window-item
              v-if="stepNumbers['Profile']"
              :value="stepNumbers['Profile']"
            >
              <ProfileStep
                ref="profileStep"
                v-model:user="user_data"
                v-model:profile="profile"
                v-model:organization="organization"
              />
            </v-window-item>

            <!-- Agreements -->
            <v-window-item 
              v-if="stepNumbers['Agreements']"
              :value="stepNumbers['Agreements']"
            >
              <LegalStep
                ref="legalStep"
                v-model="legal"
              />
            </v-window-item>

            <!-- Document Status -->
            <v-window-item 
              v-if="stepNumbers['Document Status']"
              :value="stepNumbers['Document Status']"
            >
              <DocumentStatusStep
                ref="documentStatusStep"
              />
            </v-window-item>
          </v-window>
        </template>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-6">
        <v-btn
          v-if="stepNumbers[currentStepName] > (stepNumbers['Profile'] ?? 0) && !hasProfile"
          variant="text"
          class="text-none"
          :disabled="isLoading"
          @click="step--"
        >
          <v-icon start>
            mdi-arrow-left
          </v-icon>
          Back
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          :disabled="!canProceed"
          :loading="isLoading"
          size="large"
          class="text-none px-6"
          @click="handleNext"
        >
          {{ getStepActionText }}
          <v-icon
            end
            :icon="getStepActionIcon"
          />
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { SystemError } from '@/models/error'
import { useLegalDocumentStore } from '@/stores/legal_document'
import { useAgreementStore } from '@/stores/agreement'
import AgreementStep from './steps/AgreementStep.vue'
import ProfileStep from './steps/ProfileStep.vue'
import LegalStep from './steps/LegalStep.vue'
import DocumentStatusStep from './steps/DocumentStatusStep.vue'
import { brand } from '@/config/brand'
import type { ILegalDocumentInitiateSigningData } from '@/services/legal_document/'

const dialog = ref(true)
const step = ref(1)
const isLoading = ref(true)

const authStore = useAuthStore()
const legalDocumentStore = useLegalDocumentStore()
const agreementStore = useAgreementStore()

const agreementStep = ref<InstanceType<typeof AgreementStep> | null>(null)
const profileStep = ref<InstanceType<typeof ProfileStep> | null>(null)
const legalStep = ref<InstanceType<typeof LegalStep> | null>(null)
const documentStatusStep = ref<InstanceType<typeof DocumentStatusStep> | null>(null)

// TODO: LF-2025-03-28 - Remove this once full name is being set in the Auth0 signup process
const user_data = ref({
  first_name: '',
  last_name: ''
})

const profile = ref({
  about: '',
  country: '',
  business_title: '',
  job_description: ''
})

const organization = ref({
  name: ''
})

const legal = ref({
  hasSigningAuthority: null as 'yes' | 'no' | null,
  authorizedSigner: {
    name: '',
    email: '',
    title: ''
  },
  consent: false
})

const agreementConsent = ref({
  consent: false
})

// Note: 'Agreement' (singular, SD-19-05 click-through gate) is intentionally
// distinct from the existing 'Agreements' (plural, LegalStep signing flow).
type StepName = 'Agreement' | 'Profile' | 'Agreements' | 'Document Status'

const hasProfile = computed(() => !!authStore.getUser?.profile)
const hasSignedDocuments = computed(() => !!authStore.getUser?.organization?.signed_required_documents)
const hasSignableDocuments = computed(() => legalDocumentStore.getSignableDocuments.length > 0)

// Presence-driven hard gate (SD-19-05): shown iff `click_through_agreement`
// on the `auth/user/` payload (SD-19-03) says an active agreement is
// required and not yet accepted by this user. No brand key involved.
//
// This intentionally reads the SAME field as `HomeView.showOnboarding`
// (rather than re-deriving from `agreementStore.getAgreement`'s own
// `required`/`accepted`, which reflects the separate `v1/agreements/active/`
// fetch, SD-19-04) so there is exactly one source of truth for "does this
// user still need to accept": HomeView decides whether to open this dialog
// at all from it, and this decides whether the step is present once open.
// Two independent sources here previously meant they could disagree (most
// concretely: after `recordAcceptance` below, only the agreement store's
// copy flips — `authStore.getUser` stayed stale for the rest of the SPA
// session, so navigating away from Home and back reopened this dialog with
// zero steps left: a persistent, un-closeable, blank modal). The
// agreement store is still used for the step's *content* (title/body/
// version) in `AgreementStep.vue` — only the gate decision moved here.
const needsAgreementAcceptance = computed(() => {
  const status = authStore.getUser?.click_through_agreement
  return !!status?.required && !status?.accepted
})

// Determine which steps should be shown
const activeSteps = computed(() => {
  const steps: StepName[] = []

  // Step 0 (first): unaccepted click-through agreement blocks everything else.
  if (needsAgreementAcceptance.value) {
    steps.push('Agreement')
  }

  // Step 1: Profile (show if no profile exists)
  if (!hasProfile.value) {
    steps.push('Profile')
  }

  // For document-related steps, we need to check both conditions
  if (!hasSignedDocuments.value) {
    if (!hasSignableDocuments.value) {
      // If no signable documents, show Agreements
      steps.push('Agreements')
    }
    steps.push('Document Status')
  }

  return steps
})

// Map step names to step numbers (1-based)
const stepNumbers = computed(() => {
  const map: Record<StepName, number> = {
    'Agreement': 0,
    'Profile': 0,
    'Agreements': 0,
    'Document Status': 0
  }
  activeSteps.value.forEach((step, index) => {
    map[step] = index + 1
  })
  return map
})

const currentStepName = computed(() => 
  activeSteps.value.find(name => stepNumbers.value[name] === step.value) ?? activeSteps.value[0]
)

const stepTitle = computed(() => {
  switch (currentStepName.value) {
    case 'Agreement':
      return 'Review Required'
    case 'Profile':
      return `Welcome to ${brand.productName}`
    case 'Agreements':
      return 'Peraso Legal Agreement Process'
    case 'Document Status':
      return 'Peraso Legal Agreement Status'
    default:
      return ''
  }
})

const stepSubtitle = computed(() => {
  switch (currentStepName.value) {
    case 'Agreement':
      return 'Please review and accept the following agreement to continue.'
    case 'Profile':
      return 'Almost there. Some additional information is needed to access this content.'
    case 'Agreements':
      return 'Please review and initiate the document signing process with the appropriate signing authority.'
    case 'Document Status':
      return 'Track the status of your document signing requests below.'
    default:
      return ''
  }
})

const canProceed = computed(() => {
  switch (currentStepName.value) {
    case 'Agreement':
      return agreementConsent.value.consent
    case 'Profile':
      return !!profile.value.about &&
             !!profile.value.country &&
             !!profile.value.business_title &&
             !!profile.value.job_description &&
             !!organization.value.name
    case 'Agreements':
      if (!legal.value.hasSigningAuthority || !legal.value.consent) return false
      if (legal.value.hasSigningAuthority === 'no') {
        return !!legal.value.authorizedSigner.name &&
               !!legal.value.authorizedSigner.email &&
               !!legal.value.authorizedSigner.title
      }
      return true
    case 'Document Status':
      return legalDocumentStore.getSignableDocuments.every(doc => doc.is_signed)
    default:
      return false
  }
})

const getStepActionText = computed(() => {
  switch (currentStepName.value) {
    case 'Agreement':
      return 'I Agree'
    case 'Profile':
      return 'Complete Profile'
    case 'Agreements':
      return 'Initiate Document Signing'
    case 'Document Status':
      return 'Complete'
    default:
      return 'Continue'
  }
})

const getStepActionIcon = computed(() => {
  switch (currentStepName.value) {
    case 'Agreement':
      return 'mdi-check'
    case 'Agreements':
      return 'mdi-file-sign'
    default:
      return 'mdi-arrow-right'
  }
})

async function handleNext() {
  let isValid = true
  isLoading.value = true

  try {
    switch (currentStepName.value) {
      case 'Agreement': {
        // Guard the null-agreement path: if the SD-19-04 content fetch failed
        // or returned null, `AgreementStep` shows its error/retry fallback
        // (no consent control renders). Never silently skip `recordAcceptance`
        // and advance/close as if accepted — bail so the user stays on the
        // gate with visible feedback instead of the acceptance being lost.
        const activeAgreement = agreementStore.getAgreement
        if (!activeAgreement) {
          isValid = false
          break
        }
        isValid = (await agreementStep.value?.form?.validate())?.valid ?? false
        if (isValid) {
          try {
            await agreementStore.recordAcceptance(activeAgreement.version)
          } catch (error) {
            // 409 = the terms changed since they were shown. The store has
            // already re-fetched the current version into view; force a
            // conscious re-consent (clear the checkbox) and keep the user on the
            // gate rather than advancing — they must read and accept the NEW
            // terms. Any other error bubbles to the outer catch.
            if (error instanceof SystemError && error.statusCode === 409) {
              agreementConsent.value.consent = false
              isValid = false
              break
            }
            throw error
          }
          // Refresh the cached auth user so `click_through_agreement.accepted`
          // — the single source of truth `needsAgreementAcceptance` (and
          // `HomeView.showOnboarding`) reads — reflects the acceptance just
          // recorded. Without this, `authStore.getUser` stays stale for the
          // rest of the SPA session and re-mounting Home (no full reload)
          // would reopen this dialog. `activeSteps`/`stepNumbers` have
          // already recomputed by the time we read them below (the
          // 'Agreement' step is now gone).
          await authStore.fetchUser()
          if (activeSteps.value.length > 0) {
            if (!hasProfile.value) {
              step.value = stepNumbers.value['Profile']
            } else if (hasSignableDocuments.value) {
              step.value = stepNumbers.value['Document Status']
            } else {
              step.value = stepNumbers.value['Agreements']
            }
          } else {
            dialog.value = false
          }
        }
        break
      }
      case 'Profile':
        isValid = (await profileStep.value?.form?.validate())?.valid ?? false
        if (isValid) {
          await authStore.updateUser({
            first_name: user_data.value.first_name,
            last_name: user_data.value.last_name,
            profile: {
              about: profile.value.about,
              country: profile.value.country,
              business_title: profile.value.business_title,
              job_description: profile.value.job_description
            },
            organization: {
              name: organization.value.name
            }
          })
          if (activeSteps.value.length > 0) {
            // Move to next step if available
            if (hasSignableDocuments.value) {
              step.value = stepNumbers.value['Document Status']
            } else {
              step.value = stepNumbers.value['Agreements']
            }
          } else {
            dialog.value = false
          }
        }
        break
      case 'Agreements':
        isValid = (await legalStep.value?.form?.validate())?.valid ?? false
        if (isValid) {
          const signerData: ILegalDocumentInitiateSigningData = legal.value.hasSigningAuthority === 'yes' 
            ? {
                signer_name: `${authStore.getUser!.first_name} ${authStore.getUser!.last_name}`,
                signer_email: authStore.getUser!.email,
                signer_title: authStore.getUser!.profile!.business_title
              }
            : {
                signer_name: legal.value.authorizedSigner.name,
                signer_email: legal.value.authorizedSigner.email,
                signer_title: legal.value.authorizedSigner.title
              }

          await legalDocumentStore.initiateDocumentSigning(signerData)
          step.value = stepNumbers.value['Document Status']
        }
        break
      case 'Document Status':
        if (legalDocumentStore.getSignableDocuments.every(doc => doc.is_signed)) {
          dialog.value = false
        }
        break
    }
  } catch (error) {
    console.error('Failed to process step:', error)
    isValid = false
  } finally {
    isLoading.value = false
  }
}

onMounted(async () => {
  try {
    let user = authStore.getUser
    if (!user) {
      await authStore.fetchUser()
      user = authStore.getUser
    }

    // Initialize organization data if it exists
    if (user?.organization) {
      organization.value.name = user.organization.name
    }

    // Fetch the active agreement in ISOLATION: a network/5xx failure here must
    // NOT abort the rest of onboarding init (especially fetchSignableDocuments
    // below), or users with unsigned documents would misroute to the Agreements
    // initiation step instead of Document Status. The store fails closed to
    // null; AgreementStep surfaces an error+retry if the gate is still required.
    try {
      await agreementStore.fetchActiveAgreement()
    } catch (error) {
      console.error('Failed to fetch the active agreement:', error)
    }

    // Always fetch signable documents if documents aren't signed
    // This ensures we have the correct state for computing steps
    if (!user?.organization?.signed_required_documents) {
      await legalDocumentStore.fetchSignableDocuments()
    }

    // Set initial step to the first step in our active steps
    if (activeSteps.value.length > 0) {
      step.value = stepNumbers.value[activeSteps.value[0]]
    }
  } catch (error) {
    console.error('Failed to initialize onboarding:', error)
  } finally {
    isLoading.value = false
  }
})
</script>

<style scoped>
.onboarding-wizard {
  border-radius: 16px;
  
  :deep(.v-card) {
    background: rgb(var(--v-theme-surface));
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  }

  :deep(.v-field) {
    border-radius: 8px;
  }

  :deep(.v-btn) {
    text-transform: none;
    font-weight: 500;
  }
}

.stepper-container {
  width: 100%;
  max-width: 500px;
  padding: 0 32px;
}

.stepper-step {
  position: relative;
  width: 120px;

  .step-circle {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgb(var(--v-theme-surface));
    border: 2px solid rgba(var(--v-border-color), var(--v-border-opacity));
    font-size: 0.875rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
    transition: all 0.3s ease;
  }

  .step-content {
    width: 100%;
  }

  .step-label {
    color: rgb(var(--v-theme-on-surface-variant));
    transition: color 0.3s ease;
  }

  &.active {
    .step-circle {
      border-color: rgb(var(--v-theme-primary));
      color: rgb(var(--v-theme-primary));
    }

    .step-label {
      color: rgb(var(--v-theme-primary));
    }
  }

  &.completed {
    .step-circle {
      background: rgb(var(--v-theme-primary));
      border-color: rgb(var(--v-theme-primary));
    }
  }

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}

.step-connector {
  flex: 1;
  height: 2px;
  background: rgba(var(--v-border-color), var(--v-border-opacity));
  margin: 0 -12px;
  margin-bottom: 28px;
  transition: background-color 0.3s ease;

  &.completed {
    background: rgb(var(--v-theme-primary));
  }
}

:deep(.v-window__container) {
  height: auto !important;
}

.onboarding-window {
  :deep(.v-window-item) {
    padding: 0 16px;
  }
}

.flex-1 {
  flex: 1;
}

:deep(.v-card-title) {
  font-size: 1rem;
}
</style> 